import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import AccountService, { ProfileData } from '../../../services/account.service';
import ProfileTab from './ProfileTab';
import SecurityTab from './SecurityTab';
import SessionsTab from './SessionsTab';
import PreferencesTab from './PreferencesTab';

type Tab = 'profile' | 'security' | 'sessions' | 'preferences';

const TABS: { key: Tab; labelKey: string; icon: string }[] = [
  { key: 'profile', labelKey: 'account.nav.profile', icon: 'ri-user-3-line' },
  { key: 'security', labelKey: 'account.nav.security', icon: 'ri-shield-keyhole-line' },
  { key: 'sessions', labelKey: 'account.nav.sessions', icon: 'ri-computer-line' },
  { key: 'preferences', labelKey: 'account.nav.preferences', icon: 'ri-notification-3-line' },
];

const MyAccount: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  const activeTab = (searchParams.get('tab') ?? 'profile') as Tab;

  const setTab = (tab: Tab) => {
    setSearchParams({ tab });
  };

  const loadProfile = () => {
    setLoading(true);
    AccountService.getProfile()
      .then((res: any) => {
        setProfile(res.data ?? null);
      })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadProfile();
  }, []);

  return (
    <div className="flex-grow-1">
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="fw-semibold mb-1" style={{ color: 'var(--bs-heading-color)' }}>
            {t('account.title')}
          </h4>
          <p className="text-muted mb-0 small">{t('account.subtitle')}</p>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-3 mb-4 mb-md-0">
          <div className="card" style={{ borderRadius: '0.75rem' }}>
            <div className="card-body p-3">
              <nav className="d-flex flex-column gap-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`btn btn-sm text-start d-flex align-items-center gap-2 px-3 py-2 ${
                      activeTab === tab.key ? 'btn-primary' : 'btn-light'
                    }`}
                    style={{ borderRadius: '0.5rem', fontWeight: activeTab === tab.key ? 600 : 400 }}
                    onClick={() => setTab(tab.key)}
                  >
                    <i className={`${tab.icon} ri-18px`} />
                    {t(tab.labelKey)}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-9">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <>
              {activeTab === 'profile' && (
                <ProfileTab profile={profile} onProfileUpdated={loadProfile} />
              )}
              {activeTab === 'security' && (
                <SecurityTab profile={profile} onUpdated={loadProfile} />
              )}
              {activeTab === 'sessions' && <SessionsTab />}
              {activeTab === 'preferences' && <PreferencesTab />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
